import { Info } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { cn } from '../../../lib/utils';
import { SeverityBadge } from './badges';
import { SeverityResolution, SeverityScope } from './severityResolution';

export interface SeverityResolutionPopoverProps {
  errorIdentifier: string;
  resolution: SeverityResolution;
  scope: SeverityScope;
  /** Name des Kunden im Kunden-Geltungsbereich — nur für die Kopfzeile. */
  customerName?: string;
}

/**
 * „Warum diese Severity?" — macht die fünfstufige Auflösung sichtbar, statt sie
 * zu erklären: Treffer hervorgehoben, übersprungene Stufen durchgestrichen,
 * im Geltungsbereich nicht anwendbare Stufen ausgegraut.
 *
 * Die Nummerierung 1–5 bildet die tatsächliche Vorrangreihenfolge ab.
 */
export function SeverityResolutionPopover({
  errorIdentifier,
  resolution,
  scope,
  customerName,
}: SeverityResolutionPopoverProps) {
  const winnerPosition = resolution.steps.findIndex((step) => step.isWinner);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          aria-label={`Herkunft der Severity von ${errorIdentifier} anzeigen`}
          onClick={(e) => e.stopPropagation()}
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[420px] p-0">
        <div className="border-b border-gray-200 px-4 py-3">
          <div className="font-mono text-sm text-gray-900">{errorIdentifier}</div>
          <div className="mt-0.5 text-xs text-gray-500">
            Geltungsbereich: {scope === 'global' ? 'Global' : (customerName ?? 'Kunde')}
          </div>
        </div>

        <ol className="py-1">
          {resolution.steps.map((step, position) => {
            const isSkipped = !step.isWinner && !step.notApplicable && position < winnerPosition;
            const isBelowWinner = winnerPosition >= 0 && position > winnerPosition;
            return (
              <li
                key={step.level}
                className={cn(
                  'flex items-center gap-3 px-4 py-2 text-xs',
                  step.isWinner ? 'bg-green-50 text-gray-900' : 'text-gray-500',
                  (step.notApplicable || isBelowWinner) && !step.isWinner && 'opacity-50'
                )}
              >
                <span
                  className={cn(
                    'flex h-5 w-5 flex-none items-center justify-center rounded-full border text-[10px] font-bold',
                    step.isWinner
                      ? 'border-green-700 bg-green-700 text-white'
                      : 'border-gray-200 bg-white text-gray-400'
                  )}
                >
                  {position + 1}
                </span>
                <span className="min-w-0 flex-1">
                  <span className={cn('block', isSkipped && 'line-through decoration-gray-300')}>
                    {step.label}
                  </span>
                  <span className="block truncate text-[11px] text-gray-400">{step.detail}</span>
                </span>
                <span className="flex-none">
                  {step.severity ? (
                    <span className={cn(!step.isWinner && 'opacity-60')}>
                      <SeverityBadge severity={step.severity} />
                    </span>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </span>
              </li>
            );
          })}
        </ol>

        <div className="flex items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 px-4 py-2.5 text-xs text-gray-600">
          <span>Ergebnis für neue Events</span>
          <SeverityBadge severity={resolution.severity} />
        </div>
      </PopoverContent>
    </Popover>
  );
}
